import { useRef, useState, type ChangeEvent, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  Spinner,
  apiFetch,
  type ActionLinkContext,
  type ActionLinkUploadResult,
} from "@dearbill/shared";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

/** Member-facing labels for the upload link's document classes. */
const FIELD_LABELS: Record<string, string> = {
  medicare_card: "Medicare card",
  plan_card: "Plan card",
  pharmacy_receipts: "Pharmacy receipt",
  payment_bills: "Payment bill",
};

/**
 * The server's upload allowlist (`server/app/services/upload_store.py`):
 * content type and extension must both match. Checked client-side so a
 * rejected file gets a specific message without a round trip; the server
 * re-checks both and the file's magic bytes.
 */
const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".pdf"];
/** The camera input takes only photo types, so the camera app opens directly. */
const PHOTO_ACCEPT = "image/jpeg,image/png";
const FILE_ACCEPT = [...ALLOWED_TYPES, ...ALLOWED_EXTENSIONS].join(",");

function isAllowedFile(file: File): boolean {
  const dot = file.name.lastIndexOf(".");
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  return ALLOWED_TYPES.includes(file.type) && ALLOWED_EXTENSIONS.includes(ext);
}

const unsupportedTypeMessage = (name: string): string =>
  `"${name}" isn't a file type we can accept. Please use a JPEG or PNG photo, or a PDF.`;

interface ActionLinkUploadProps {
  /** The action-link bearer token, replayed on every upload. */
  token: string;
  /** The loaded link context; `field` names the accepted document class. */
  context: ActionLinkContext;
  /** Called when an upload is rejected with the opaque 401 (the link died mid-flow). */
  onExpired: () => void;
}

/**
 * The mid-call upload loop: a "Take a photo" input (`capture` opens the rear
 * camera on phones) and a separate "Choose a file" input for saved photos and
 * PDFs, one `POST /api/links/upload` per picked file, the list of what
 * landed, and "Add another" because one link accepts several pages or cards.
 * The two inputs stay separate because Android ignores the file picker when
 * `capture` is combined with `multiple` or a PDF type. Files upload one at a
 * time so a flaky connection fails one file, not the batch; a failed or
 * unsupported file is named in the error and the loop stays usable.
 */
export function ActionLinkUpload({ token, context, onExpired }: ActionLinkUploadProps): ReactNode {
  const fieldLabel = FIELD_LABELS[context.field ?? ""] ?? "document";
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploaded, setUploaded] = useState<ActionLinkUploadResult[]>([]);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const greeting = context.patientFirstName ? `Hi ${context.patientFirstName}!` : "Hi there!";

  const onPick = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(e.target.files ?? []);
    // Allow re-picking the same file after a failure or another round.
    e.target.value = "";
    if (files.length === 0) return;
    setError(null);
    for (const file of files) {
      if (!isAllowedFile(file)) {
        setError(unsupportedTypeMessage(file.name));
        break;
      }
      setBusyName(file.name);
      const form = new FormData();
      form.append("file", file);
      try {
        const result = await apiFetch<ActionLinkUploadResult>(
          "/api/links/upload",
          { method: "POST", body: form },
          { baseUrl: API_BASE, token },
        );
        setUploaded((prev) => [...prev, result]);
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 401) {
          onExpired();
          return;
        }
        if (err instanceof ApiError && err.status === 413) {
          setError(`"${file.name}" is too large. Try a smaller photo.`);
        } else if (err instanceof ApiError && err.status === 422) {
          setError(unsupportedTypeMessage(file.name));
        } else {
          setError(`We couldn't upload "${file.name}". Please try again.`);
        }
        break;
      }
    }
    setBusyName(null);
  };

  return (
    <div>
      <h1 className="text-h3 text-midnight font-serif">{greeting}</h1>
      <p className="text-body-lg text-midnight mt-3">
        Please take a photo of your <strong>{fieldLabel}</strong>. A photo from your phone camera
        works great — just make sure the whole card or page is visible.
      </p>

      {/* Hidden inputs behind the visible buttons, so each tap target is one
          obvious action. The camera input is single-file and photo-only;
          desktop browsers ignore `capture` and show the file picker. */}
      <input
        ref={cameraRef}
        type="file"
        accept={PHOTO_ACCEPT}
        capture="environment"
        onChange={(e) => void onPick(e)}
        className="sr-only"
        tabIndex={-1}
        aria-label={`Take a photo of your ${fieldLabel}`}
      />
      <input
        ref={fileRef}
        type="file"
        accept={FILE_ACCEPT}
        multiple
        onChange={(e) => void onPick(e)}
        className="sr-only"
        tabIndex={-1}
        aria-label={`Choose a photo or PDF of your ${fieldLabel}`}
      />

      {uploaded.length > 0 && (
        <div className="mt-6">
          <h2 className="text-body text-midnight font-bold">Uploaded so far</h2>
          <ul className="mt-2 flex flex-col gap-2">
            {uploaded.map((f, i) => (
              <li
                key={`${f.uploadedAt}-${i}`}
                className="border-success-border bg-success-bg text-forest flex items-center gap-3 rounded-2xl border px-4 py-3 text-[15px]"
              >
                <span aria-hidden="true">✓</span>
                <span className="min-w-0 break-all">{f.filename}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {busyName !== null && (
        <div className="text-slate-teal mt-6 flex items-center gap-3">
          <Spinner /> <span>Uploading {busyName}…</span>
        </div>
      )}

      {error && <Alert tone="error" title={error} className="mt-6" />}

      <div className="mt-8 flex flex-col gap-3">
        <Button
          size="lg"
          className="w-full"
          disabled={busyName !== null}
          onClick={() => cameraRef.current?.click()}
        >
          {uploaded.length === 0 ? "Take a photo" : "Take another photo"}
        </Button>
        <Button
          variant="outlined"
          size="lg"
          className="w-full"
          disabled={busyName !== null}
          onClick={() => fileRef.current?.click()}
        >
          {uploaded.length === 0 ? "Choose a file" : "Choose another file"}
        </Button>
      </div>
      <p className="text-caption text-slate-teal mt-3 text-center">
        JPEG or PNG photos, or PDF files.
      </p>

      {uploaded.length > 0 && (
        <p className="text-body-sm text-slate-teal mt-4 text-center">
          All set? You can close this page — we already have what you uploaded.
        </p>
      )}
    </div>
  );
}
